import * as React from "react";
import { useEffect, createContext } from "react";
import usePartySocket from "partysocket/react";
import { create } from "zustand";
import {
  type Presence,
  type User,
  type ClientMessage,
  type PartyMessage,
  decodeMessage,
  encodeClientMessage,
  partyMessageSchema,
} from "./presence-schema";

type UserMap = Map<string, User>;

type PresenceStoreType = {
  myId: string | null;
  myself: User | null;
  setMyId: (myId: string) => void;

  synced: boolean;
  setSynced: (synced: boolean) => void;

  pendingUpdate: Presence | null;
  clearPendingUpdate: () => void;

  updatePresence: (partial: Partial<Presence>) => void;

  otherUsers: UserMap;

  setUsers: (users: UserMap) => void;

  addUser: (id: string, user: User) => void;
  removeUser: (id: string) => void;
  updateUser: (id: string, presence: Presence) => void;
};

export const usePresence = create<PresenceStoreType>((set) => ({
  myId: null,
  myself: null,
  setMyId: (myId: string) => set({ myId }),

  synced: false,
  setSynced: (synced: boolean) => set({ synced }),

  pendingUpdate: null,
  clearPendingUpdate: () => set({ pendingUpdate: null }),
  updatePresence: (partial: Partial<Presence>) =>
    set((state) => {
      if (!state.myself) return {};
      const presence = {
        ...state.myself.presence,
        ...partial,
      } as Presence;
      const myself = {
        ...state.myself,
        presence,
      };
      return { myself, pendingUpdate: presence };
    }),

  otherUsers: new Map() as UserMap,

  setUsers: (users: UserMap) =>
    set((state) => {
      const otherUsers = new Map<string, User>();
      users.forEach((user, id) => {
        if (id === state.myId) return;
        otherUsers.set(id, user);
      });
      const myself = state.myId ? users.get(state.myId) : null;
      return { myself, otherUsers };
    }),

  addUser: (id: string, user: User) => {
    set((state) => {
      if (id === state.myId) {
        return {};
      }
      const otherUsers = new Map(state.otherUsers);
      otherUsers.set(id, user);
      return { otherUsers };
    });
  },
  removeUser: (id: string) => {
    set((state) => {
      if (id === state.myId) {
        return {};
      }
      const otherUsers = new Map(state.otherUsers);
      otherUsers.delete(id);
      return { otherUsers };
    });
  },
  updateUser: (id: string, presence: Presence) => {
    set((state) => {
      if (id === state.myId) {
        return {};
      }
      const otherUsers = new Map(state.otherUsers);
      const user = otherUsers.get(id);
      if (!user) return { otherUsers };
      otherUsers.set(id, { ...user, presence });
      return { otherUsers };
    });
  },
}));

export const PresenceContext = createContext({});

export default function PresenceProvider(props: {
  host: string;
  room: string;
  presence: Presence;
  children: React.ReactNode;
}) {
  const {
    setMyId,
    setUsers,
    addUser,
    updateUser,
    removeUser,
    pendingUpdate,
    clearPendingUpdate,
    synced,
    setSynced,
    otherUsers, // Get otherUsers from the store
  } = usePresence();

  // Modified assignCursorIndex with better collision avoidance
  const assignCursorIndex = () => {
    const stored = localStorage.getItem("cursorIndex");
    if (stored !== null) {
      const storedIndex = Number(stored);
      // Check if the stored cursor is still available
      const usedIndices = new Set<number>();
      otherUsers.forEach(user => {
        if (user.presence.cursorIndex !== undefined) {
          usedIndices.add(user.presence.cursorIndex);
        }
      });
      
      // If stored cursor is still available, use it
      if (!usedIndices.has(storedIndex)) {
        return storedIndex;
      }
    }

    const usedIndices = new Set<number>();
    // Collect cursor indices of currently connected users
    otherUsers.forEach(user => {
      if (user.presence.cursorIndex !== undefined) {
        usedIndices.add(user.presence.cursorIndex);
      }
    });

    const totalCursorConfigs = 10; // Based on CURSOR_CONFIGS in cursor.tsx
    
    // Find first available cursor index
    for (let i = 0; i < totalCursorConfigs; i++) {
      if (!usedIndices.has(i)) {
        localStorage.setItem("cursorIndex", String(i));
        return i;
      }
    }
    
    // If all cursors are taken, assign a random one (fallback)
    const chosen = Math.floor(Math.random() * totalCursorConfigs);
    localStorage.setItem("cursorIndex", String(chosen));
    return chosen;
  };

  const initialPresence = {
    ...props.presence,
    cursorIndex: props.presence.cursorIndex ?? assignCursorIndex(),
  };

  const updateUsers = (message: PartyMessage) => {
    if (message.type !== "changes") return;
    if (message.add) {
      for (const [id, user] of Object.entries(message.add)) {
        addUser(id, user);
      }
    }
    if (message.presence) {
      for (const [id, presence] of Object.entries(message.presence)) {
        updateUser(id, presence);
      }
    }
    if (message.remove) {
      for (const id of message.remove) {
        removeUser(id);
      }
    }
  };

  const handleMessage = async (event: MessageEvent) => {
    const data =
      event.data instanceof Blob
        ? decodeMessage(await event.data.arrayBuffer())
        : JSON.parse(event.data);

    const result = partyMessageSchema.safeParse(data);
    if (!result.success) return;
    const message = result.data;

    switch (message.type) {
      case "sync":
        setMyId(socket.id);
        setUsers(new Map<string, User>(Object.entries(message.users)));
        setSynced(true);
        break;
      case "changes":
        updateUsers(message);
        break;
    }
  };

  const socket = usePartySocket({
    host: props.host,
    room: props.room,
    query: {
      name: props.presence.name,
      color: props.presence.color,
      cursorIndex: String(initialPresence.cursorIndex),
      from: window?.location.href,
    },
    onMessage: (event) => handleMessage(event),

    onClose: (e) =>
      console.warn(
        "Socket closed:",
        e.reason ||
          // @ts-ignore
          e.error ||
          e
      ),
    onError: (e) =>
      console.error(
        "Socket error:",
        // @ts-ignore
        e.reason ||
          // @ts-ignore
          e.error ||
          e
      ),
  });

  useEffect(() => {
    if (socket) {
      setMyId(socket.id);
      if (!synced) {
        const message: ClientMessage = {
          type: "update",
          presence: initialPresence,
        };
        socket.send(encodeClientMessage(message));
      }
    }
  }, [initialPresence, setMyId, synced, socket]);

  useEffect(() => {
    if (!pendingUpdate) return;
    if (!socket) return;
    const message: ClientMessage = { type: "update", presence: pendingUpdate };
    socket.send(encodeClientMessage(message));
    clearPendingUpdate();
  }, [socket, pendingUpdate, clearPendingUpdate]);

  return (
    <PresenceContext.Provider value={{}}>
      {props.children}
    </PresenceContext.Provider>
  );
}